import { ApolloError } from '@apollo/client';

import { describeError, getRateLimitRejection } from '../../../common/rateLimit';
import { count } from '../manage/manage';

/**
 * Workflows (plan 4.5, API card 17.5) as plain functions: what a step is, how it
 * reads, what the API will refuse, and what each state allows. Pure, so it is
 * tested without rendering.
 *
 * The rules mirror `stepRows`, `triggerConfig` and `guardFields` in the API's
 * src/crm/crm-workflows.ts. Change both together.
 */

export type StepType = 'DELAY' | 'SEND_EMAIL' | 'SEND_SMS' | 'SEND_PUSH' | 'ADD_TAG' | 'REMOVE_TAG' | 'CREATE_CALL_TASK' | 'END';
export type TriggerType = 'MANUAL' | 'TAG_ADDED' | 'SEGMENT';
export type WorkflowStatus = 'DRAFT' | 'RUNNING' | 'PAUSED' | 'ENDED' | 'ARCHIVED';
export type EnrollmentStatus = 'ACTIVE' | 'COMPLETED' | 'EXCLUDED' | 'CANCELED' | 'FAILED';

export const LIMITS = {
  name: 200,
  description: 1000,
  steps: 50,
  delayMinutes: 525_600,
  smsBody: 1600,
  pushTitle: 100,
  pushBody: 500,
  note: 500,
  dueInHours: 720,
  exitTags: 10,
  reentryCooldownH: 8760,
  maxPerContactPerDay: 50,
  maxSendsPerDay: 1_000_000,
} as const;

/** One step as the editor holds it. `key` is the editor's own, stable while steps move. */
export interface StepDraft {
  key: string;
  type: StepType;
  delayMinutes?: number | null;
  delayUntilDow?: number | null;
  delayUntilHour?: number | null;
  emailCampaignId?: number | null;
  emailCampaignName?: string | null;
  emailSubject?: string | null;
  message?: string | null;
  title?: string | null;
  tagId?: number | null;
  tagName?: string | null;
  note?: string | null;
  dueInHours?: number | null;
  assignToAdminId?: number | null;
  /** On a saved workflow: people waiting at this step, and how often it has run. */
  waiting?: number;
  executed?: number;
}

/** A step as the API answers it. */
export interface WorkflowStep extends Omit<StepDraft, 'key'> {
  id: number;
  position: number;
  waiting: number;
  executed: number;
  nbAfterEnrolmentMinutes?: number | null;
}

export interface WorkflowSettings {
  name: string;
  description: string;
  triggerType: TriggerType;
  triggerSegmentId: number | null;
  triggerSegmentName: string | null;
  triggerTagId: number | null;
  triggerTagName: string | null;
  includeExisting: boolean;
  exitTagIds: Array<number>;
  allowReentry: boolean;
  reentryCooldownH: number | null;
  maxPerContactPerDay: number | null;
  maxSendsPerDay: number | null;
  quietHoursStart: number | null;
  quietHoursEnd: number | null;
}

export interface EnrollmentCounts {
  active: number;
  completed: number;
  excluded: number;
  canceled: number;
  failed: number;
  total: number;
}

export interface Workflow extends Omit<WorkflowSettings, 'description' | 'triggerType'> {
  id: number;
  description: string | null;
  source: string;
  status: WorkflowStatus;
  nbStatus: string | null;
  triggerType: TriggerType | 'EVENT';
  stepCount: number;
  enrollments: EnrollmentCounts;
  startedAt: string | null;
  pausedAt: string | null;
  endedAt: string | null;
  lastEnrolledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------ *
 * What each step is
 * ------------------------------------------------------------------ */

export interface StepMeta {
  label: string;
  /** One line in the add-step menu. */
  hint: string;
  /** The step's colour on the canvas. */
  tone: string;
  /** Sends a message: waits out quiet hours and counts against the caps. */
  sends: boolean;
}

export const STEP_META: Record<StepType, StepMeta> = {
  SEND_EMAIL: { label: 'Send email', hint: 'A draft campaign, sent to this one person', tone: '#4f46e5', sends: true },
  SEND_SMS: { label: 'Send text', hint: 'An SMS to people opted in to texts', tone: '#0891b2', sends: true },
  SEND_PUSH: { label: 'Send app notification', hint: 'A push, and a note in their app inbox', tone: '#7c3aed', sends: true },
  ADD_TAG: { label: 'Add tag', hint: 'Recorded on their timeline as the workflow', tone: '#059669', sends: false },
  REMOVE_TAG: { label: 'Remove tag', hint: 'Takes a tag off them', tone: '#dc2626', sends: false },
  CREATE_CALL_TASK: { label: 'Call task', hint: 'A follow-up in the call queue', tone: '#ea580c', sends: false },
  DELAY: { label: 'Wait', hint: 'A time, or until a weekday and hour', tone: '#d97706', sends: false },
  END: { label: 'End', hint: 'Finishes the workflow for them', tone: '#64748b', sends: false },
};

/** The order of the add-step menu. */
export const STEP_CHOICES: Array<StepType> = ['SEND_EMAIL', 'SEND_SMS', 'SEND_PUSH', 'DELAY', 'ADD_TAG', 'REMOVE_TAG', 'CREATE_CALL_TASK', 'END'];

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

let nextKey = 0;
export const newKey = () => `step-${Date.now().toString(36)}-${(nextKey++).toString(36)}`;

/** A new step with sensible defaults: a wait is a day, a call task due in a day. */
export const newStep = (type: StepType): StepDraft => {
  const base: StepDraft = { key: newKey(), type };
  if (type === 'DELAY') return { ...base, delayMinutes: 1440 };
  if (type === 'CREATE_CALL_TASK') return { ...base, dueInHours: 24 };
  return base;
};

export const fromApiStep = (step: WorkflowStep): StepDraft => ({
  key: `saved-${step.id}`,
  type: step.type,
  delayMinutes: step.delayMinutes ?? null,
  delayUntilDow: step.delayUntilDow ?? null,
  delayUntilHour: step.delayUntilHour ?? null,
  emailCampaignId: step.emailCampaignId ?? null,
  emailCampaignName: step.emailCampaignName ?? null,
  emailSubject: step.emailSubject ?? null,
  message: step.message ?? null,
  title: step.title ?? null,
  tagId: step.tagId ?? null,
  tagName: step.tagName ?? null,
  note: step.note ?? null,
  dueInHours: step.dueInHours ?? null,
  assignToAdminId: step.assignToAdminId ?? null,
  waiting: step.waiting,
  executed: step.executed,
});

const trimmed = (value: string | null | undefined) => (value ?? '').trim();

/** What the API takes for a step: only the fields its type reads. */
export const toStepInput = (step: StepDraft) => {
  switch (step.type) {
    case 'DELAY':
      return step.delayMinutes != null
        ? { type: step.type, delayMinutes: step.delayMinutes }
        : { type: step.type, delayUntilDow: step.delayUntilDow ?? null, delayUntilHour: step.delayUntilHour ?? null };
    case 'SEND_EMAIL':
      return { type: step.type, emailCampaignId: step.emailCampaignId ?? null };
    case 'SEND_SMS':
      return { type: step.type, message: trimmed(step.message) };
    case 'SEND_PUSH':
      return { type: step.type, title: trimmed(step.title), message: trimmed(step.message) };
    case 'ADD_TAG':
    case 'REMOVE_TAG':
      return { type: step.type, tagId: step.tagId ?? null };
    case 'CREATE_CALL_TASK':
      return {
        type: step.type,
        dueInHours: step.dueInHours ?? 0,
        note: trimmed(step.note) || null,
        assignToAdminId: step.assignToAdminId ?? null,
      };
    default:
      return { type: step.type };
  }
};

/* ------------------------------------------------------------------ *
 * How a step reads
 * ------------------------------------------------------------------ */

const unit = (n: number, word: string) => `${count(n)} ${word}${n === 1 ? '' : 's'}`;

/** "1 day", "2 hours 30 minutes", "3 weeks": the two largest parts. */
export const formatDuration = (minutes: number): string => {
  if (minutes <= 0) return 'no time';
  if (minutes % 10080 === 0) return unit(minutes / 10080, 'week');
  const parts: Array<string> = [];
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days) parts.push(unit(days, 'day'));
  if (hours) parts.push(unit(hours, 'hour'));
  if (mins) parts.push(unit(mins, 'minute'));
  return parts.slice(0, 2).join(' ');
};

/** 0 → "midnight", 10 → "10 am", 12 → "noon", 21 → "9 pm". */
export const formatHour = (hour: number): string => {
  if (hour === 0) return 'midnight';
  if (hour === 12) return 'noon';
  return hour < 12 ? `${hour} am` : `${hour - 12} pm`;
};

/** A wait's amount in the largest unit that divides it: 2880 → 2 days. */
export type DelayUnit = 'minutes' | 'hours' | 'days' | 'weeks';
export const UNIT_MINUTES: Record<DelayUnit, number> = { minutes: 1, hours: 60, days: 1440, weeks: 10080 };

export const splitMinutes = (minutes: number | null | undefined): { amount: number; unit: DelayUnit } => {
  const m = minutes ?? 1440;
  for (const u of ['weeks', 'days', 'hours'] as const) {
    if (m >= UNIT_MINUTES[u] && m % UNIT_MINUTES[u] === 0) return { amount: m / UNIT_MINUTES[u], unit: u };
  }
  return { amount: m, unit: 'minutes' };
};

export const describeDelay = (step: Pick<StepDraft, 'delayMinutes' | 'delayUntilDow' | 'delayUntilHour'>): string => {
  if (step.delayMinutes != null) return `Wait ${formatDuration(step.delayMinutes)}`;
  if (step.delayUntilHour == null) return 'Wait';
  if (step.delayUntilDow != null) return `Wait until ${DAYS[step.delayUntilDow]} at ${formatHour(step.delayUntilHour)}`;
  return `Wait until ${formatHour(step.delayUntilHour)} the next day`;
};

const clip = (text: string, max = 80) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/** The line under a step's name on the canvas. */
export const describeStep = (step: StepDraft): string => {
  switch (step.type) {
    case 'DELAY':
      return describeDelay(step);
    case 'SEND_EMAIL':
      return step.emailCampaignId
        ? step.emailSubject
          ? `“${clip(step.emailSubject)}”`
          : step.emailCampaignName ?? `Campaign #${step.emailCampaignId}`
        : 'Choose the email to send';
    case 'SEND_SMS':
      return trimmed(step.message) ? `“${clip(trimmed(step.message))}”` : 'Write the text';
    case 'SEND_PUSH':
      return trimmed(step.title) ? clip(trimmed(step.title)) : 'Write the notification';
    case 'ADD_TAG':
    case 'REMOVE_TAG':
      return step.tagId ? step.tagName ?? `Tag #${step.tagId}` : 'Choose a tag';
    case 'CREATE_CALL_TASK': {
      const due = step.dueInHours ?? 0;
      const when = due === 0 ? 'due at once' : `due in ${due % 24 === 0 ? unit(due / 24, 'day') : unit(due, 'hour')}`;
      return trimmed(step.note) ? `${when}: ${clip(trimmed(step.note), 60)}` : `A follow-up, ${when}`;
    }
    case 'END':
      return 'They leave the workflow';
    default:
      return '';
  }
};

/**
 * When each step runs, counted from enrolment: the sum of the waits before it.
 * Null after a wait until a weekday, which depends on when each person came in.
 */
export const stepOffsets = (steps: ReadonlyArray<StepDraft>): Array<number | null> => {
  let at: number | null = 0;
  return steps.map((step) => {
    const here = at;
    if (step.type === 'DELAY') at = at === null || step.delayMinutes == null ? null : at + step.delayMinutes;
    return here;
  });
};

export const describeOffset = (minutes: number | null): string =>
  minutes === null ? 'Depends on when they join' : minutes === 0 ? 'As they join' : `${formatDuration(minutes)} in`;

/* ------------------------------------------------------------------ *
 * What the API would refuse
 * ------------------------------------------------------------------ */

export interface StepProblem {
  field: string;
  message: string;
}

const wholeIn = (v: unknown, min: number, max: number) => Number.isInteger(v) && (v as number) >= min && (v as number) <= max;

/** Any `{{ }}` or `{% %}` other than the name fields: texts and pushes fill names only. */
export const badTextFields = (text: string): Array<string> => {
  const out: Array<string> = [];
  const add = (tag: string) => {
    if (!out.includes(tag)) out.push(tag);
  };
  const output = /\{\{\s*([^{}]*?)\s*\}\}/g;
  const logic = /\{%[\s\S]*?%\}/g;
  let m: RegExpExecArray | null;
  while ((m = output.exec(text))) {
    const name = m[1].split('|')[0].trim().replace(/^recipient\./, '');
    if (!['first_name', 'last_name', 'full_name', 'name', 'first_name_or_friend'].includes(name)) add(m[0]);
  }
  while ((m = logic.exec(text))) add(m[0]);
  return out;
};

const textProblem = (value: string | null | undefined, max: number, field: string, label: string): StepProblem | null => {
  const t = trimmed(value);
  if (!t) return { field, message: `${label} is needed.` };
  if (t.length > max) return { field, message: `${label} is at most ${count(max)} characters.` };
  const bad = badTextFields(t);
  if (bad.length) return { field, message: `Only name fields work here: ${bad.join(', ')}` };
  return null;
};

/** The first thing wrong with one step, or null. */
export const stepProblem = (step: StepDraft): StepProblem | null => {
  switch (step.type) {
    case 'DELAY':
      if (step.delayMinutes != null) {
        return wholeIn(step.delayMinutes, 1, LIMITS.delayMinutes) ? null : { field: 'delayMinutes', message: 'A wait is 1 minute to a year.' };
      }
      if (step.delayUntilHour == null || !wholeIn(step.delayUntilHour, 0, 23)) return { field: 'delayUntilHour', message: 'Choose the hour.' };
      return null;
    case 'SEND_EMAIL':
      return step.emailCampaignId ? null : { field: 'emailCampaignId', message: 'Choose the email to send.' };
    case 'SEND_SMS':
      return textProblem(step.message, LIMITS.smsBody, 'message', 'The text');
    case 'SEND_PUSH':
      return textProblem(step.title, LIMITS.pushTitle, 'title', 'A title') ?? textProblem(step.message, LIMITS.pushBody, 'message', 'A message');
    case 'ADD_TAG':
    case 'REMOVE_TAG':
      return step.tagId ? null : { field: 'tagId', message: 'Choose a tag.' };
    case 'CREATE_CALL_TASK':
      if (!wholeIn(step.dueInHours ?? 0, 0, LIMITS.dueInHours)) return { field: 'dueInHours', message: 'Due in 0 to 720 hours.' };
      if (trimmed(step.note).length > LIMITS.note) return { field: 'note', message: `A note is at most ${LIMITS.note} characters.` };
      return null;
    default:
      return null;
  }
};

/** Problems with the whole line of steps, by index; -1 for the workflow as a whole. */
export const workflowProblems = (steps: ReadonlyArray<StepDraft>): Map<number, string> => {
  const out = new Map<number, string>();
  if (steps.length === 0) {
    out.set(-1, 'Add at least one step.');
    return out;
  }
  if (steps.length > LIMITS.steps) out.set(-1, `At most ${LIMITS.steps} steps.`);
  steps.forEach((step, i) => {
    const problem = stepProblem(step);
    if (problem) out.set(i, problem.message);
    if (step.type === 'END' && i !== steps.length - 1) out.set(i, 'End can only be the last step.');
  });
  const last = steps[steps.length - 1];
  if (last.type === 'DELAY' && !out.has(steps.length - 1)) out.set(steps.length - 1, 'A workflow cannot end on a wait: nothing would follow it.');
  if (steps.every((s) => s.type === 'DELAY' || s.type === 'END') && !out.has(-1)) {
    out.set(-1, 'Add a step that does something: an email, a text, a tag or a call task.');
  }
  return out;
};

/** Problems with the settings, by field. */
export const settingsProblems = (s: WorkflowSettings): Partial<Record<keyof WorkflowSettings, string>> => {
  const out: Partial<Record<keyof WorkflowSettings, string>> = {};
  const name = s.name.trim();
  if (!name) out.name = 'Give the workflow a name.';
  else if (name.length > LIMITS.name) out.name = `At most ${LIMITS.name} characters.`;
  if (s.description.trim().length > LIMITS.description) out.description = `At most ${LIMITS.description} characters.`;
  if (s.triggerType === 'TAG_ADDED' && !s.triggerTagId) out.triggerTagId = 'Choose the tag that enrols people.';
  if (s.triggerType === 'SEGMENT' && !s.triggerSegmentId) out.triggerSegmentId = 'Choose the list or saved filter.';
  if (s.reentryCooldownH != null && !wholeIn(s.reentryCooldownH, 0, LIMITS.reentryCooldownH)) out.reentryCooldownH = '0 to 8,760 hours.';
  if (s.allowReentry && s.triggerType === 'SEGMENT' && (s.reentryCooldownH ?? 0) < 24) {
    out.reentryCooldownH = 'A list trigger with re-entry needs a cooldown of at least 24 hours.';
  }
  if (s.maxPerContactPerDay != null && !wholeIn(s.maxPerContactPerDay, 1, LIMITS.maxPerContactPerDay)) out.maxPerContactPerDay = '1 to 50 messages.';
  if (s.maxSendsPerDay != null && !wholeIn(s.maxSendsPerDay, 1, LIMITS.maxSendsPerDay)) out.maxSendsPerDay = 'At least 1.';
  if ((s.quietHoursStart == null) !== (s.quietHoursEnd == null)) out.quietHoursStart = 'Quiet hours need a start and an end, or neither.';
  else if (s.quietHoursStart != null && s.quietHoursStart === s.quietHoursEnd) out.quietHoursEnd = 'They cannot start and end at the same hour.';
  if (s.exitTagIds.length > LIMITS.exitTags) out.exitTagIds = `At most ${LIMITS.exitTags} tags.`;
  return out;
};

/** The settings as `CrmWorkflowInput`. */
export const settingsInput = (s: WorkflowSettings) => ({
  name: s.name.trim(),
  description: s.description.trim() || null,
  triggerType: s.triggerType,
  trigger:
    s.triggerType === 'TAG_ADDED'
      ? { tagId: s.triggerTagId, includeExisting: s.includeExisting }
      : s.triggerType === 'SEGMENT'
        ? { segmentId: s.triggerSegmentId, includeExisting: s.includeExisting }
        : null,
  exitTagIds: s.exitTagIds,
  allowReentry: s.allowReentry,
  reentryCooldownH: s.allowReentry ? s.reentryCooldownH : null,
  maxPerContactPerDay: s.maxPerContactPerDay,
  maxSendsPerDay: s.maxSendsPerDay,
  quietHoursStart: s.quietHoursStart,
  quietHoursEnd: s.quietHoursEnd,
});

export const DEFAULT_SETTINGS: WorkflowSettings = {
  name: '',
  description: '',
  triggerType: 'MANUAL',
  triggerSegmentId: null,
  triggerSegmentName: null,
  triggerTagId: null,
  triggerTagName: null,
  includeExisting: false,
  exitTagIds: [],
  allowReentry: false,
  reentryCooldownH: null,
  maxPerContactPerDay: 2,
  maxSendsPerDay: null,
  quietHoursStart: 21,
  quietHoursEnd: 8,
};

export const settingsOf = (w: Workflow): WorkflowSettings => ({
  name: w.name,
  description: w.description ?? '',
  triggerType: w.triggerType === 'EVENT' ? 'MANUAL' : w.triggerType,
  triggerSegmentId: w.triggerSegmentId,
  triggerSegmentName: w.triggerSegmentName,
  triggerTagId: w.triggerTagId,
  triggerTagName: w.triggerTagName,
  includeExisting: w.includeExisting,
  exitTagIds: w.exitTagIds,
  allowReentry: w.allowReentry,
  reentryCooldownH: w.reentryCooldownH,
  maxPerContactPerDay: w.maxPerContactPerDay,
  maxSendsPerDay: w.maxSendsPerDay,
  quietHoursStart: w.quietHoursStart,
  quietHoursEnd: w.quietHoursEnd,
});

/* ------------------------------------------------------------------ *
 * Triggers and guards, in words
 * ------------------------------------------------------------------ */

export const describeTrigger = (s: Pick<WorkflowSettings, 'triggerType' | 'triggerTagName' | 'triggerTagId' | 'triggerSegmentName' | 'triggerSegmentId'>) => {
  switch (s.triggerType) {
    case 'TAG_ADDED':
      return s.triggerTagId ? `When someone is tagged ${s.triggerTagName ?? `#${s.triggerTagId}`}` : 'When someone is given a tag';
    case 'SEGMENT':
      return s.triggerSegmentId ? `When someone joins ${s.triggerSegmentName ?? `list #${s.triggerSegmentId}`}` : 'When someone joins a list';
    default:
      return 'Enrolled by hand';
  }
};

/** The guards as short chips: "No re-entry", "Quiet 9 pm – 8 am", "2 a day each". */
export const describeGuards = (s: WorkflowSettings, tagName: (id: number) => string): Array<string> => {
  const out: Array<string> = [];
  out.push(s.allowReentry ? (s.reentryCooldownH ? `Re-entry after ${formatDuration(s.reentryCooldownH * 60)}` : 'Re-entry allowed') : 'Once per person');
  if (s.quietHoursStart != null && s.quietHoursEnd != null) out.push(`Quiet ${formatHour(s.quietHoursStart)} – ${formatHour(s.quietHoursEnd)}`);
  if (s.maxPerContactPerDay) out.push(`${s.maxPerContactPerDay} message${s.maxPerContactPerDay === 1 ? '' : 's'} a day each`);
  if (s.maxSendsPerDay) out.push(`${count(s.maxSendsPerDay)} sends a day in all`);
  if (s.exitTagIds.length) out.push(`Leaves when tagged ${s.exitTagIds.map(tagName).join(', ')}`);
  return out;
};

/* ------------------------------------------------------------------ *
 * States
 * ------------------------------------------------------------------ */

export const STATUS_META: Record<WorkflowStatus, { label: string; tone: string }> = {
  DRAFT: { label: 'Draft', tone: '#64748b' },
  RUNNING: { label: 'Running', tone: '#059669' },
  PAUSED: { label: 'Paused', tone: '#d97706' },
  ENDED: { label: 'Ended', tone: '#475569' },
  ARCHIVED: { label: 'Archived', tone: '#94a3b8' },
};

export const ENROLLMENT_META: Record<EnrollmentStatus, { label: string; tone: string }> = {
  ACTIVE: { label: 'In it', tone: '#4f46e5' },
  COMPLETED: { label: 'Finished', tone: '#059669' },
  EXCLUDED: { label: 'Left (exit tag)', tone: '#d97706' },
  CANCELED: { label: 'Taken out', tone: '#64748b' },
  FAILED: { label: 'Failed', tone: '#dc2626' },
};

export const isMigrated = (w: Pick<Workflow, 'source'>) => w.source === 'NATIONBUILDER';

/** Steps change only on a draft nobody has been enrolled in (the API's rule). */
export const canEditSteps = (w: Pick<Workflow, 'source' | 'status' | 'enrollments'>) =>
  !isMigrated(w) && w.status === 'DRAFT' && w.enrollments.total === 0;

/** Name, trigger and guards change while a draft or paused. */
export const canEditSettings = (w: Pick<Workflow, 'source' | 'status'>) => !isMigrated(w) && (w.status === 'DRAFT' || w.status === 'PAUSED');

export type WorkflowAction = 'start' | 'resume' | 'pause' | 'end' | 'archive' | 'enrol' | 'copy' | 'delete';

/** What can be done to a workflow now, in the order the buttons show. */
export const actionsFor = (w: Pick<Workflow, 'source' | 'status' | 'enrollments'>): Array<WorkflowAction> => {
  if (isMigrated(w)) return ['copy'];
  switch (w.status) {
    case 'DRAFT':
      return ['start', 'copy', ...(w.enrollments.total === 0 ? (['delete'] as const) : (['end'] as const))];
    case 'RUNNING':
      return ['enrol', 'pause', 'copy', 'end'];
    case 'PAUSED':
      return ['resume', 'enrol', 'copy', 'end'];
    case 'ENDED':
      return ['copy', 'archive'];
    default:
      return ['copy'];
  }
};

/* ------------------------------------------------------------------ *
 * Refusals
 * ------------------------------------------------------------------ */

const firstError = (error: unknown) => (error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined);

export const errorCode = (error: unknown) => firstError(error)?.extensions?.code as string | undefined;

/** The step a refusal is about: `steps[2].delayMinutes` → 2. */
export const stepOfError = (error: unknown): number | null => {
  const field = firstError(error)?.extensions?.field;
  const m = typeof field === 'string' ? /^steps\[(\d+)\]/.exec(field) : null;
  return m ? Number(m[1]) : null;
};

/** The new number when an enrolment was refused because the selection changed. */
export const changedCount = (error: unknown): number | undefined => {
  if (errorCode(error) !== 'CRM_SELECTION_CHANGED') return undefined;
  const n = firstError(error)?.extensions?.count;
  return typeof n === 'number' ? n : undefined;
};

export const describeWorkflowError = (error: unknown, fallback = 'The change could not be made.'): string => {
  if (getRateLimitRejection(error)) return describeError(error, 'Too many changes at once');
  const first = firstError(error);
  switch (errorCode(error)) {
    case 'CRM_SENDING_DISABLED':
      return 'Workflows are switched off on this server (CRM_WORKFLOWS_ENABLED), or email is when a step sends one. Nothing is wrong with the workflow.';
    case 'CRM_WORKFLOW_STATE':
      return first?.extensions?.reason === 'MIGRATED'
        ? 'This workflow came from NationBuilder and is kept as history. Copy it to use it.'
        : first?.message ?? 'The workflow is not in a state that allows this.';
    case 'CRM_WORKFLOW_NOT_FOUND':
      return 'That workflow no longer exists. Someone may have just deleted it.';
    case 'CRM_SELECTION_CHANGED': {
      const n = changedCount(error);
      return n === undefined
        ? 'The number of people changed. Nobody was enrolled.'
        : `It matches ${count(n)} people now, not the number you confirmed. Nobody was enrolled: check the new number and confirm again.`;
    }
    case 'CRM_IN_USE':
    case 'CRM_INVALID_QUERY':
    case 'CRM_CAMPAIGN_STATE':
    case 'CRM_CAMPAIGN_INCOMPLETE':
      return first?.message ?? fallback;
    case 'FORBIDDEN':
      return 'Only admins can change workflows.';
    default:
      if (error instanceof ApolloError && error.networkError) return 'Could not reach the API. Check before trying again.';
      return first?.message ?? fallback;
  }
};
